import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { NAV_ITEMS } from '@/lib/navigation-config';
import { getStaticSearchItems, staticItemMatchesQuery } from '@/lib/search-routes';
import { useAuth } from '@/stores/use-auth';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useGlobalSearch,
  type GlobalSearchHit,
  type GlobalSearchResultType,
} from '@/features/search/hooks/useGlobalSearch';
import {
  trackPaletteDismissedWithoutSelection,
  trackPaletteEntrySelected,
  trackPaletteOpened,
  trackSearchExecuted,
} from '@codemerlin/shared/analytics';

interface SearchPaletteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_GROUP_LABEL_KEY: Record<GlobalSearchResultType, string> = {
  engineer: 'search.groups.engineers',
  team: 'search.groups.teams',
  repository: 'search.groups.repositories',
  pull_request: 'search.groups.pullRequests',
  work_item: 'search.groups.workItems',
};

const TYPE_ICON: Record<GlobalSearchResultType, keyof typeof Icons> = {
  engineer: 'User',
  team: 'Network',
  repository: 'FolderGit2',
  pull_request: 'GitPullRequest',
  work_item: 'CheckSquare',
};

const TYPE_ORDER: GlobalSearchResultType[] = [
  'engineer',
  'team',
  'repository',
  'pull_request',
  'work_item',
];

/** Block `/organization/:id/...` navigation when id does not match the active session org (SC-004). */
function isNavigationPathAllowedForSession(
  path: string,
  organizationId: string | undefined
): boolean {
  const match = /^\/organization\/([^/]+)/.exec(path);
  if (!match) {
    return true;
  }
  if (!organizationId) {
    return false;
  }
  return match[1] === organizationId;
}

export function SearchPalette({ isOpen, onOpenChange }: SearchPaletteProps) {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const organizationId = user?.organization?.organizationId;
  const memberRole = user?.organization?.role;
  const eligibleForNextShell = user?.nextNavigation?.eligibleForNextShell ?? false;
  const catalogOnlyMode = eligibleForNextShell;

  const [inputValue, setInputValue] = useState('');
  const debouncedQuery = useDebounce(inputValue, 300);
  const iconMap = Icons as unknown as Record<string, LucideIcon>;

  const selectedThisOpenRef = useRef(false);
  const prevOpenRef = useRef(isOpen);

  const { data: apiResults = [], isFetching } = useGlobalSearch(organizationId, debouncedQuery, {
    enabled: !catalogOnlyMode,
  });

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      selectedThisOpenRef.current = false;
      trackPaletteOpened({ next_shell: catalogOnlyMode });
    }
    if (!isOpen && prevOpenRef.current && !selectedThisOpenRef.current) {
      trackPaletteDismissedWithoutSelection({ next_shell: catalogOnlyMode });
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, catalogOnlyMode]);

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const staticItems = useMemo(
    () => getStaticSearchItems(organizationId, eligibleForNextShell, memberRole),
    [organizationId, eligibleForNextShell, memberRole]
  );

  const translatedStaticItems = useMemo(
    () => staticItems.map((item) => ({ ...item, label: t(item.label) })),
    [staticItems, t]
  );

  const filteredStatic = useMemo(
    () => translatedStaticItems.filter((item) => staticItemMatchesQuery(item, inputValue)),
    [translatedStaticItems, inputValue]
  );

  const groupedApi = useMemo(() => {
    const map = new Map<GlobalSearchResultType, GlobalSearchHit[]>();
    for (const type of TYPE_ORDER) {
      map.set(type, []);
    }
    for (const hit of apiResults) {
      const list = map.get(hit.type);
      if (list) {
        list.push(hit);
      }
    }
    return map;
  }, [apiResults]);

  const handleSelectStatic = (path: string, entryId: string) => {
    if (!isNavigationPathAllowedForSession(path, organizationId)) {
      return;
    }
    selectedThisOpenRef.current = true;
    trackPaletteEntrySelected({
      entry_id: entryId,
      destination_key: path,
      next_shell: catalogOnlyMode,
      group: 'navigation',
    });
    onOpenChange(false);
    navigate(path);
  };

  const handleSelectApiHit = (path: string, hit: GlobalSearchHit) => {
    if (!isNavigationPathAllowedForSession(path, organizationId)) {
      return;
    }
    selectedThisOpenRef.current = true;
    trackSearchExecuted({
      search_scope: 'global_data',
      result_type: hit.type,
      query_length: debouncedQuery.trim().length,
    });
    onOpenChange(false);
    navigate(path);
  };

  const showDataSearch =
    !catalogOnlyMode && Boolean(organizationId) && debouncedQuery.trim().length >= 2;

  const showOrgHint = catalogOnlyMode && (organizationId === undefined || organizationId === '');

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={onOpenChange}
      commandProps={{
        shouldFilter: false,
      }}
      accessibilityTitle={t('search.paletteA11yTitle')}
      accessibilityDescription={
        catalogOnlyMode
          ? t('search.paletteA11yDescriptionCatalog')
          : t('search.paletteA11yDescriptionHybrid')
      }
    >
      <CommandInput
        aria-label={t('search.filterInputAriaLabel')}
        placeholder={catalogOnlyMode ? t('search.placeholderCatalogStub') : t('search.placeholder')}
        value={inputValue}
        onValueChange={setInputValue}
      />
      {showOrgHint ? (
        <p className="border-b px-4 py-2 text-xs text-muted-foreground">
          {t('search.orgContextHint')}
        </p>
      ) : null}
      <CommandList>
        <CommandEmpty>{t('search.noResults')}</CommandEmpty>

        {filteredStatic.length > 0 && (
          <CommandGroup heading={t('search.pagesGroup')}>
            {filteredStatic.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <CommandItem
                  key={item.id}
                  value={`static-${item.id}-${item.label}`}
                  onSelect={() => handleSelectStatic(item.path, item.id)}
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  <span>{item.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {showDataSearch && isFetching && (
          <CommandGroup heading={t('search.searching')}>
            <CommandItem disabled value="loading-placeholder">
              {t('search.fetchingResults')}
            </CommandItem>
          </CommandGroup>
        )}

        {showDataSearch &&
          !isFetching &&
          TYPE_ORDER.map((type) => {
            const hits = groupedApi.get(type) ?? [];
            if (hits.length === 0) {
              return null;
            }
            const IconComponent = iconMap[TYPE_ICON[type]];
            return (
              <CommandGroup key={type} heading={t(TYPE_GROUP_LABEL_KEY[type])}>
                {hits.map((hit) => (
                  <CommandItem
                    key={`${hit.type}-${hit.id}`}
                    value={`${hit.type}-${hit.id}-${hit.label}`}
                    onSelect={() => handleSelectApiHit(hit.path, hit)}
                  >
                    {IconComponent && <IconComponent className="mr-2 h-4 w-4" />}
                    <span className="flex flex-col gap-0.5">
                      <span>{hit.label}</span>
                      {hit.description ? (
                        <span className="text-xs text-muted-foreground">{hit.description}</span>
                      ) : null}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}

        {NAV_ITEMS.filter((item) => item.disabled).length > 0 && inputValue.length === 0 && (
          <CommandGroup heading={t('search.comingSoon')}>
            {NAV_ITEMS.filter((item) => item.disabled).map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <CommandItem
                  key={item.id}
                  disabled
                  value={`disabled-${item.id}`}
                  className="opacity-50"
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  <span>{t(item.label)}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
